import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  addLeagueOpponents,
  addEsplayLeagueOpponents,
  refreshEsplayCatalog,
  addOpponentDemo,
  autoAssignMatch,
  clearScoutingDemos,
  createOpponent,
  deleteMatch,
  deleteOpponent,
  fetchMatches,
  fetchOpponent,
  fetchOpponents,
  fetchScoreboard,
  fetchParseStatus,
  fetchScouting,
  fetchSeason,
  fetchUnfiled,
  fetchVeto,
  findTeamGames,
  removeLeagueOpponents,
  removeOpponentDemo,
  uploadDemo,
  type MatchListItem,
  type UnfiledDemo,
} from '../data/api'
import type { Opponent, OpponentSummary, Scouting as ScoutingData, ScoutMapAgg, TeamGame, VetoSheet } from '../types'
import { ConfirmButton } from './ConfirmButton'
import { SectionLabel } from './RoundTimeline'
import { useCanCoach } from '../data/session'
import { UtilMap } from './UtilMap'
import { Economy } from './Economy'
import { DemoDiff } from './DemoDiff'
import { Lineups } from './Lineups'
import { SpacingCurve } from './SpacingCurve'
import { clutchTitle, fmtDate, fmtStamp, median, paceLabel } from '../data/fmt'

const card = { borderRadius: 'var(--radius)', boxShadow: 'var(--shadow)' } as const
const inputCls =
  'border border-border bg-bg-2 px-3 py-2 text-sm text-text outline-none focus:border-accent'

/** Poll until the parser publishes the scoreboard; resolves with the demo's team names.
 *  A crashed parse used to be indistinguishable from a slow one — both just ran out the clock —
 *  so read status.json and fail loudly with the actual error instead.
 *
 *  Asks `status` FIRST and only then the scoreboard. The other order meant every poll of a demo
 *  that was still parsing was a 404 on `/matches/{id}/scoreboard` — 307 of them in 30 days, 67 for
 *  a single demo — which is a lot of log noise shaped exactly like a bug, for a question `status`
 *  answers with a 200. The scoreboard is still what resolves this, because the team names are what
 *  the caller needs and PARSED is written after the artifacts. */
function waitParsedTeams(demoId: string): Promise<string[]> {
  return new Promise((resolve, reject) => {
    const started = Date.now()
    const poll = setInterval(async () => {
      const st = await fetchParseStatus(demoId).catch(() => null)
      if (st?.state === 'FAILED') {
        clearInterval(poll)
        reject(new Error(st.error ? `parse failed — ${st.error}` : 'parse failed'))
        return
      }
      if (st?.state === 'PARSED') {
        try {
          const sb = await fetchScoreboard(demoId)
          clearInterval(poll)
          resolve(Object.keys(sb.team_score))
          return
        } catch {
          /* artifacts not readable yet — try again on the next tick */
        }
      }
      if (Date.now() - started > 12 * 60_000) {
        clearInterval(poll)
        reject(new Error('still parsing after 12 min — check back shortly'))
      }
    }, 8000)
  })
}

// ---------------- Opponent list ----------------
export function Scouting({ onOpen }: { onOpen: (id: string) => void }) {
  const [list, setList] = useState<OpponentSummary[]>([])
  const [loaded, setLoaded] = useState(false)
  const [name, setName] = useState('')
  const refresh = useCallback(() => {
    fetchOpponents()
      .then(setList)
      .finally(() => setLoaded(true))
    fetchUnfiled().then(setUnfiled).catch(() => setUnfiled([]))
  }, [])
  useEffect(() => refresh(), [refresh])

  const [busy, setBusy] = useState(false)
  const [note, setNote] = useState<string | null>(null)
  // Feedback for the bulk actions, shown beside the buttons that trigger them. `note` renders up in
  // the "New opponent folder" section, so using it for these reported a failure somewhere off-screen
  // — which is indistinguishable from the click doing nothing at all.
  const [opNote, setOpNote] = useState<string | null>(null)
  const [unfiledNote, setUnfiledNote] = useState<string | null>(null)
  const [unfiled, setUnfiled] = useState<UnfiledDemo[]>([])
  const [up, setUp] = useState<{ label: string; pct: number } | null>(null)
  const [elapsed, setElapsed] = useState(0)
  const dropRef = useRef<HTMLInputElement>(null)
  useEffect(() => {
    if (!up) return
    const t = setInterval(() => setElapsed((e) => e + 1), 1000)
    return () => clearInterval(t)
  }, [up])

  // Drop a league demo without picking a folder first: the demo names both teams, so it files
  // itself into theirs — opening the folders that don't exist yet.
  const onFiles = async (files: File[]) => {
    const demos = files.filter((f) => /\.dem(\.zst|\.gz)?$/.test(f.name))
    if (!demos.length) {
      setNote('Pick .dem / .dem.gz / .dem.zst files')
      return
    }
    setNote(null)

    // Uploads first, then wait on all the parses together.
    //
    // This used to be one loop: upload, wait for that demo to finish parsing, upload the next. A
    // scouting session is a dozen demos in one sitting, so that was twelve × (upload + ~2 min
    // parse) in sequence — and the parser job is configured to run three executions in parallel, so
    // two thirds of the capacity sat idle while the browser waited. The uploads themselves stay
    // sequential: they are ~300 MB each and racing them just divides the same connection.
    setElapsed(0)
    const queued: { file: File; demoId: string }[] = []
    for (let i = 0; i < demos.length; i++) {
      const n = demos.length > 1 ? ` ${i + 1}/${demos.length}` : ''
      try {
        setUp({ label: `Uploading${n}`, pct: 0 })
        const { demoId } = await uploadDemo(demos[i], (pct) => setUp({ label: `Uploading${n}`, pct }), 'scouting')
        queued.push({ file: demos[i], demoId })
      } catch (e) {
        setNote(`${demos[i].name}: ${(e as Error).message}`)
      }
    }

    setElapsed(0)
    setUp({ label: queued.length > 1 ? `Parsing ${queued.length} demos` : 'Parsing', pct: 100 })
    const filed: string[] = []
    const failed: string[] = []
    // Settled, not all: one unparseable demo in a session of twelve must not lose the other eleven.
    const results = await Promise.allSettled(
      queued.map(async ({ demoId }) => {
        await waitParsedTeams(demoId)
        return autoAssignMatch(demoId, true)
      }),
    )
    results.forEach((r, i) => {
      const name = queued[i].file.name
      if (r.status === 'rejected') {
        failed.push(`${name}: ${(r.reason as Error)?.message ?? 'failed'}`)
      } else {
        filed.push(
          r.value.length
            ? r.value.map((h) => `${h.opponent}${h.created ? ' (new)' : ''}`).join(' + ')
            : `${name}: only our own team in it — nothing to scout`,
        )
      }
    })
    setUp(null)
    // Both halves reported: a session where ten filed and two failed must say so, not pick one.
    setNote(
      [filed.length ? `Filed into ${filed.join('; ')}.` : '', ...failed].filter(Boolean).join(' ') || null,
    )
    refresh()
  }

  const create = async () => {
    const v = name.trim()
    if (!v) return
    setNote(null)
    try {
      // A FACEIT team link/id resolves the real team (and its id); anything else is a plain folder.
      const isFaceit = /faceit\.com\/.*\/teams\/|^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(v)
      const o = await createOpponent(v, isFaceit ? v : undefined)
      setName('')
      refresh()
      onOpen(o.id)
    } catch (e) {
      setNote((e as Error).message)
    }
  }
  const addDivision = async () => {
    setBusy(true)
    setNote(null)
    try {
      const r = await addLeagueOpponents()
      setNote(
        `${r.league}: added ${r.created.length}${r.skipped.length ? `, skipped ${r.skipped.length} already here` : ''}.`,
      )
      refresh()
    } catch (e) {
      setNote((e as Error).message)
    } finally {
      setBusy(false)
    }
  }
  /** The Nuke-liiga twin of `addDivision`.
   *
   *  Refreshes the catalog first rather than assuming it is there. It is rebuilt nightly, but it
   *  starts empty — and an empty catalog here would silently create nothing and report "added 0",
   *  which reads as "we already have them" rather than "there was nothing to read". */
  const addNukeLiiga = async () => {
    setBusy(true)
    setNote(null)
    try {
      const c = await refreshEsplayCatalog()
      const r = await addEsplayLeagueOpponents()
      setNote(
        `${r.league}: added ${r.created.length}${r.skipped.length ? `, skipped ${r.skipped.length} already here` : ''} ` +
          `(catalog: ${c.fixtures} fixtures, ${c.upcoming} still to play).`,
      )
      refresh()
    } catch (e) {
      setNote((e as Error).message)
    } finally {
      setBusy(false)
    }
  }
  const dropSeason = async (league: string) => {
    setBusy(true)
    setOpNote(`Clearing “${league}”…`)
    try {
      // Runs as several requests for a big season, so say what it's up to — this used to be one
      // silent call that could die on the HTTP timeout and leave the page looking merely idle.
      const n = await removeLeagueOpponents(league, (done) =>
        setOpNote(`Clearing “${league}” — ${done} folder(s) removed…`),
      )
      setOpNote(`Removed ${n} folder(s) from “${league}”.`)
    } catch (e) {
      setOpNote(`Clearing “${league}” failed: ${(e as Error).message}. Some folders may already be gone — try again.`)
    } finally {
      setBusy(false)
      refresh()
    }
  }
  /** Drop every scouting demo at once. The per-season buttons cannot: a demo lives as long as any
   *  folder references it, and folders opened by a dropped demo carry no season label — so they held
   *  every demo alive and clearing a season removed folders and no data. */
  const clearAllScouting = async () => {
    setBusy(true)
    setOpNote('Clearing scouting demos…')
    try {
      const r = await clearScoutingDemos((done) => setOpNote(`Clearing scouting demos — ${done} removed…`))
      setOpNote(`Removed ${r.removed} scouting demo(s) from ${r.detachedFrom} folder(s).`)
    } catch (e) {
      setOpNote(`Clearing scouting demos failed: ${(e as Error).message}. Anything already removed stays removed — try again.`)
    } finally {
      setBusy(false)
      refresh()
    }
  }

  /** Clear the whole strays list. One request per demo — deliberately not a single bulk call, since
   *  that is exactly the shape that made clearing a season outlive the HTTP timeout. Each one stands
   *  alone, so a failure part-way costs only that demo and the rest still go. */
  const dropUnfiled = async () => {
    setBusy(true)
    let done = 0
    const failed: string[] = []
    for (const d of unfiled) {
      setUnfiledNote(`Deleting unfiled demos — ${done}/${unfiled.length}…`)
      try {
        await deleteMatch(d.demoId)
        done++
      } catch (e) {
        failed.push(`${d.demoId.slice(0, 8)}: ${(e as Error).message}`)
      }
    }
    setUnfiledNote(
      failed.length
        ? `Deleted ${done} of ${unfiled.length} — ${failed.length} failed (${failed[0]}).`
        : `Deleted ${done} unfiled demo(s).`,
    )
    setBusy(false)
    refresh()
  }
  // seasons present, so a finished one can be cleared in one go
  const seasons = [...new Set(list.map((o) => o.league).filter(Boolean))] as string[]
  // Reading a folder is for the team; opening, filling and deleting one is staff work — and the
  // API agrees, so hiding these only saves a player a 403.
  const canCoach = useCanCoach()

  return (
    <div className="space-y-5">
      {canCoach && (
      <section className="border border-border bg-surface p-6" style={card}>
        <SectionLabel n="S0" label="New opponent folder" />
        <div className="mt-4 flex gap-2">
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && create()}
            placeholder="Team name, or paste a FACEIT team link"
            className={`flex-1 ${inputCls}`}
            style={{ borderRadius: 'var(--radius)' }}
          />
          <button
            onClick={create}
            className="border border-border-bright px-4 py-2 text-sm text-accent hover:bg-surface-2"
            style={{ borderRadius: 'var(--radius)' }}
          >
            Create
          </button>
          <button
            onClick={addDivision}
            disabled={busy}
            title="Create a folder for every other team in our current FACEIT division"
            className="border border-border px-4 py-2 text-sm text-text-dim hover:bg-surface-2 disabled:opacity-40"
            style={{ borderRadius: 'var(--radius)' }}
          >
            {busy ? 'adding…' : '+ my division'}
          </button>
          <button
            onClick={addNukeLiiga}
            disabled={busy}
            title="Create a folder for every other team in Nuke-liiga, and refresh the schedule"
            className="border border-border px-4 py-2 text-sm text-text-dim hover:bg-surface-2 disabled:opacity-40"
            style={{ borderRadius: 'var(--radius)' }}
          >
            {busy ? 'adding…' : '+ nuke-liiga'}
          </button>
        </div>
        <p className="mt-2 text-xs text-text-faint">
          “My division” pulls every team from our current ESEA season, tagged so the whole set can be dropped when
          it ends. “Nuke-liiga” does the same from esplay — those folders have no demos behind them, so their veto
          sheets are built from the league's own results instead.
        </p>

        {/* Folder-free upload: the demo names both teams, so it files itself. */}
        <div
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => {
            e.preventDefault()
            onFiles(Array.from(e.dataTransfer.files))
          }}
          onClick={() => !up && dropRef.current?.click()}
          className="mt-4 flex cursor-pointer flex-col items-center justify-center border border-dashed border-border-bright py-8 text-center hover:bg-surface-2"
          style={{ borderRadius: 'var(--radius)' }}
        >
          <input
            ref={dropRef}
            type="file"
            accept=".dem,.gz,.zst"
            multiple
            className="hidden"
            onChange={(e) => onFiles(Array.from(e.target.files ?? []))}
          />
          {up ? (
            <div className="w-72">
              <div className="text-sm text-accent">
                {up.label}… {up.label.startsWith('Parsing') ? `${elapsed}s` : `${up.pct}%`}
              </div>
              {up.label.startsWith('Parsing') ? (
                <div className="mt-1 text-xs text-text-faint">a full match takes ~1–2 min to parse — hang tight</div>
              ) : (
                <div className="mt-2 h-1.5 w-full overflow-hidden bg-border" style={{ borderRadius: '2px' }}>
                  <div className="h-full bg-accent" style={{ width: `${up.pct}%` }} />
                </div>
              )}
            </div>
          ) : (
            <>
              <div className="font-display text-sm text-text">Or drop any scouting demo here</div>
              <div className="mt-1 text-xs text-text-faint">
                one or many · it reads both team names and files itself, creating folders it needs · stays out of
                Matches
              </div>
            </>
          )}
        </div>
        {note && <p className="mt-2 text-xs text-accent">{note}</p>}
      </section>
      )}

      {canCoach && unfiled.length > 0 && (
        <section className="border p-5" style={{ ...card, borderColor: 'var(--loss)' }}>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <SectionLabel n="!" label="Unfiled demos" />
            <ConfirmButton
              label={`delete all ${unfiled.length}`}
              question={`Delete all ${unfiled.length} unfiled demo(s) and their parsed data?`}
              onConfirm={dropUnfiled}
              disabled={busy}
              title="Delete every demo in this list"
              className="text-[11px] text-text-faint hover:text-loss disabled:opacity-50"
            />
          </div>
          <p className="mt-2 text-xs text-text-faint">
            Uploaded for scouting but in no folder — a parse that failed, or a tab closed mid-upload. They're
            hidden from Matches by design, so this is the only place they show up.
          </p>
          {unfiledNote && <p className="mt-2 text-xs text-accent">{unfiledNote}</p>}
          <div className="mt-3 divide-y divide-border">
            {unfiled.map((d) => (
              <div key={d.demoId} className="flex items-center gap-3 py-2 text-sm">
                <span className="font-mono text-[11px] text-text-faint">{d.demoId.slice(0, 8)}</span>
                <span className="text-text">{d.map ?? '—'}</span>
                {d.teams?.length ? <span className="text-text-dim">{d.teams.join(' vs ')}</span> : null}
                <span
                  className="rounded px-1.5 py-px text-[10px] uppercase tracking-wider"
                  style={{
                    background: 'var(--border)',
                    color: d.state === 'FAILED' ? 'var(--loss)' : 'var(--text-dim)',
                  }}
                  title={d.error ?? ''}
                >
                  {d.state ?? 'unknown'}
                </span>
                {d.error && <span className="truncate text-[11px] text-loss" title={d.error}>{d.error}</span>}
                <span className="ml-auto flex shrink-0 gap-2">
                  {d.teams?.length ? (
                    <button
                      onClick={async () => {
                        await autoAssignMatch(d.demoId, true).catch(() => {})
                        refresh()
                      }}
                      className="text-xs text-accent hover:underline"
                    >
                      file it
                    </button>
                  ) : null}
                  <ConfirmButton
                    label="delete"
                    question="Delete this demo and its parsed data?"
                    onConfirm={async () => {
                      await deleteMatch(d.demoId).catch(() => {})
                      refresh()
                    }}
                    className="text-xs text-text-faint hover:text-loss"
                  />
                </span>
              </div>
            ))}
          </div>
        </section>
      )}

      <section className="border border-border bg-surface p-5" style={card}>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <SectionLabel n="S1" label="Opponents" />
          {canCoach && (
            <ConfirmButton
              label="clear ALL scouting demos"
              question="Delete every scouting demo? (our own matches stay)"
              onConfirm={clearAllScouting}
              disabled={busy}
              title="Delete every scouting demo in every folder — our own matches are not affected"
              className="text-[11px] text-text-faint hover:text-loss disabled:opacity-50"
            />
          )}
          {canCoach &&
            seasons.map((s) => (
            <ConfirmButton
              key={s}
              label={`clear “${s.length > 34 ? `${s.slice(0, 34)}…` : s}”`}
              question={`Remove all folders from “${s.length > 34 ? `${s.slice(0, 34)}…` : s}”?`}
              onConfirm={() => dropSeason(s)}
              disabled={busy}
              title={`Remove every folder from ${s}`}
              className="text-[11px] text-text-faint hover:text-loss disabled:opacity-50"
            />
            ))}
        </div>
        {opNote && <p className="mt-2 text-xs text-accent">{opNote}</p>}
        <div className="mt-4 divide-y divide-border">
          {list.map((o) => (
            <div key={o.id} className="flex items-center gap-1">
              <button
                onClick={() => onOpen(o.id)}
                className="flex flex-1 items-center gap-3 py-3 text-left hover:bg-surface-2"
              >
                <span className="font-display text-sm uppercase tracking-wider text-text">{o.name}</span>
                {o.league && (
                  <span
                    title={o.league}
                    className="max-w-48 truncate rounded px-1.5 py-px text-[10px] uppercase tracking-wider"
                    style={{ background: 'var(--border)', color: 'var(--text-dim)' }}
                  >
                    {o.league}
                  </span>
                )}
                <span className="ml-auto shrink-0 text-xs text-text-faint">
                  {o.demoCount} demo{o.demoCount === 1 ? '' : 's'}
                  {o.maps.length ? ` · ${o.maps.join(', ')}` : ''}
                </span>
              </button>
              {canCoach && (
                <ConfirmButton
                  label="✕"
                  question={`Delete “${o.name}” and its scouting demos?`}
                  onConfirm={async () => {
                    await deleteOpponent(o.id).catch(() => {})
                    refresh()
                  }}
                  title="Delete opponent (and its scouting demos)"
                  className="px-2 py-1 text-text-faint hover:text-loss"
                />
              )}
            </div>
          ))}
          {!list.length && (
            <div className="py-4 text-sm text-text-faint">
              {loaded ? 'No opponents yet — create a folder above.' : 'Loading…'}
            </div>
          )}
        </div>
      </section>
    </div>
  )
}

// ---------------- Opponent detail ----------------

/** What the veto table's THEM column was built from. A team that plays both leagues has two
 *  sources and the sheet adds them up; crediting only one is how a coach comes to distrust a row
 *  that was right — the cache record was there, under the wrong name. */
const sourceLabel = (s?: ('faceit' | 'league')[]): string => {
  const has = (k: string) => !!s?.includes(k as 'faceit' | 'league')
  if (!s?.length) return 'FACEIT team records' // pre-sources sheet, or nothing known
  if (has('faceit') && has('league')) return 'FACEIT team records + Nuke-liiga results'
  return has('league') ? 'Nuke-liiga results' : 'FACEIT team records'
}

/** "What else have they played?" — their roster's games across every tournament, with a link to
 *  each match room. The fetch itself stays manual (no Downloads scope), so what this removes is
 *  the part that actually scales badly: knowing the games exist at all. */
function TeamGames({ teamId, name }: { teamId: string; name: string }) {
  const [games, setGames] = useState<TeamGame[] | null>(null)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  const look = async () => {
    setBusy(true)
    setErr(null)
    try {
      setGames(await findTeamGames(teamId))
    } catch (e) {
      setErr((e as Error).message)
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="mt-3">
      <div className="flex flex-wrap items-center gap-3">
        <button
          onClick={look}
          disabled={busy}
          className="border border-border-bright px-3 py-1.5 text-xs text-accent hover:bg-surface-2 disabled:opacity-40"
          style={{ borderRadius: 'var(--radius)' }}
        >
          {busy ? 'Reading their history…' : 'Find their games in other tournaments'}
        </button>
        <span className="text-[11px] text-text-faint">
          {busy
            ? 'a call per roster member — twenty seconds or so'
            : `every competition ${name} has played, not just ours`}
        </span>
      </div>
      {err && <p className="mt-2 text-xs text-loss">{err}</p>}
      {games && !games.length && (
        <p className="mt-2 text-xs text-text-faint">
          Nothing found — FACEIT has no recent team games for this roster.
        </p>
      )}
      {games && games.length > 0 && (
        <div className="mt-2 divide-y divide-border border border-border bg-bg-2 px-3" style={{ borderRadius: 'var(--radius)' }}>
          {games.map((g) => (
            <div key={g.matchId} className="flex flex-wrap items-center gap-2 py-1.5 text-xs">
              <span className="w-24 shrink-0 text-text-faint">{fmtDate(g.finishedAt)}</span>
              <span className="text-text-dim">{g.teams.join(' vs ') || '—'}</span>
              {g.maps.length > 0 && (
                <span className="text-text-faint">{g.maps.map((m) => m.replace('de_', '')).join(', ')}</span>
              )}
              <span
                className="max-w-52 truncate rounded px-1.5 py-px text-[10px] uppercase tracking-wider"
                style={{ background: 'var(--border)', color: 'var(--text-dim)' }}
                title={g.competition}
              >
                {g.competition}
              </span>
              <span className="ml-auto shrink-0">
                {g.demo ? (
                  <a
                    href={`https://www.faceit.com/en/cs2/room/${g.matchId}`}
                    target="_blank"
                    rel="noreferrer"
                    className="text-accent hover:underline"
                    title="Open the match room, download the demo, then paste its link above"
                  >
                    get it →
                  </a>
                ) : (
                  <span className="text-text-faint" title="FACEIT has not published a demo for this match">
                    no demo published
                  </span>
                )}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export function OpponentView({ id, onBack }: { id: string; onBack: () => void }) {
  const [opp, setOpp] = useState<Opponent | null>(null)
  const [veto, setVeto] = useState<VetoSheet | null>(null)
  const [vetoErr, setVetoErr] = useState<string | null>(null)
  const [scouting, setScouting] = useState<ScoutingData | null>(null)
  const [loading, setLoading] = useState(true)
  const [err, setErr] = useState<string | null>(null)
  const [matches, setMatches] = useState<MatchListItem[]>([])

  const loadOpp = useCallback(() => fetchOpponent(id).then(setOpp).catch(() => {}), [id])
  // The veto sheet needs no demos at all — it's the one thing here that works on a team we've
  // never recorded, so it loads first and sits at the top.
  const loadVeto = useCallback(
    (refresh = false) => {
      setVetoErr(null)
      fetchVeto(id, refresh)
        .then((v) => ('error' in v ? (setVetoErr(v.error), setVeto(null)) : setVeto(v)))
        .catch(() => setVetoErr('veto_failed'))
    },
    [id],
  )
  useEffect(() => loadVeto(), [loadVeto])
  const loadReport = useCallback(
    (refresh = false) => {
      setLoading(true)
      setErr(null)
      fetchScouting(id, refresh)
        .then(setScouting)
        .catch((e) => setErr((e as Error).message))
        .finally(() => setLoading(false))
    },
    [id],
  )
  useEffect(() => {
    loadOpp()
    loadReport()
  }, [loadOpp, loadReport])
  useEffect(() => {
    fetchMatches().then(setMatches)
  }, [])

  const assignedIds = useMemo(() => new Set(opp?.demos.map((d) => d.demoId) ?? []), [opp])
  const available = matches.filter((m) => !assignedIds.has(m.id))
  const [selDemo, setSelDemo] = useState('')
  const selMatch = matches.find((m) => m.id === selDemo)
  const teams = selMatch ? Object.keys(selMatch.teamScore) : []
  const [selTeam, setSelTeam] = useState('')
  // default the scouted team to the one matching the folder name
  useEffect(() => {
    if (!teams.length) return
    const match = teams.find((t) => opp && t.toLowerCase() === opp.name.toLowerCase())
    setSelTeam(match ?? teams[0])
  }, [selDemo]) // eslint-disable-line react-hooks/exhaustive-deps

  const add = async () => {
    if (!selDemo || !selTeam) return
    await addOpponentDemo(id, selDemo, selTeam).catch(() => {})
    setSelDemo('')
    await loadOpp()
    loadReport(true) // new demo invalidates the report → rebuild
  }

  // Upload opponent demos straight into this folder (kept out of the Matches list). Multiple at once.
  const inputRef = useRef<HTMLInputElement>(null)
  const [up, setUp] = useState<
    { s: 'idle' } | { s: 'working'; label: string; pct: number } | { s: 'error'; msg: string }
  >({ s: 'idle' })
  const [pending, setPending] = useState<{ demoId: string; teams: string[] }[]>([])
  const [elapsed, setElapsed] = useState(0)
  useEffect(() => {
    if (up.s !== 'working') return
    const t = setInterval(() => setElapsed((e) => e + 1), 1000)
    return () => clearInterval(t)
  }, [up.s])


  const onFiles = async (files: File[]) => {
    const demos = files.filter((f) => /\.dem(\.zst|\.gz)?$/.test(f.name))
    if (!demos.length) {
      setUp({ s: 'error', msg: 'Pick .dem / .dem.gz / .dem.zst files' })
      return
    }
    setElapsed(0)
    const queue: { demoId: string; teams: string[] }[] = []
    for (let i = 0; i < demos.length; i++) {
      const n = `${i + 1}/${demos.length}`
      try {
        setUp({ s: 'working', label: `Uploading ${n}`, pct: 0 })
        const { demoId } = await uploadDemo(demos[i], (pct) => setUp({ s: 'working', label: `Uploading ${n}`, pct }), 'scouting')
        setUp({ s: 'working', label: `Parsing ${n}`, pct: 100 })
        const teams = await waitParsedTeams(demoId)
        const auto = teams.find((x) => x.toLowerCase() === (opp?.name ?? '').toLowerCase())
        if (auto) await addOpponentDemo(id, demoId, auto).catch(() => {})
        else queue.push({ demoId, teams })
        // also file it into the other side of a league game, opening their folder if new
        await autoAssignMatch(demoId, true).catch(() => {})
      } catch (e) {
        setUp({ s: 'error', msg: `${n}: ${(e as Error).message}` })
      }
    }
    setUp({ s: 'idle' })
    await loadOpp()
    if (queue.length) setPending(queue)
    else loadReport(true)
  }
  const confirmPending = async (team: string) => {
    const [head, ...rest] = pending
    if (!head) return
    await addOpponentDemo(id, head.demoId, team).catch(() => {})
    setPending(rest)
    await loadOpp()
    if (!rest.length) loadReport(true)
  }
  const removeDemo = async (demoId: string) => {
    await removeOpponentDemo(id, demoId).catch(() => {})
    await loadOpp()
    loadReport(true)
  }
  const del = async () => {
    await deleteOpponent(id).catch(() => {})
    onBack()
  }

  const agg = scouting?.aggregate
  const report = scouting?.report
  const canCoach = useCanCoach()

  // Our own utility, for the side-by-side. Fetched only when asked for: it's the season fold, and
  // nobody opening a scouting folder is owed that cost by default. GET, never POST — POST would
  // regenerate the AI self-review as a side effect of looking at a picture.
  const [compareUtil, setCompareUtil] = useState(false)
  const [ourMaps, setOurMaps] = useState<ScoutMapAgg[] | null>(null)
  useEffect(() => {
    if (!compareUtil || ourMaps !== null) return
    fetchSeason()
      .then((s) => setOurMaps(s.aggregate?.maps ?? []))
      .catch(() => setOurMaps([]))
  }, [compareUtil, ourMaps])
  // Only the maps we BOTH have utility on — ours on a map they've never played is a different
  // question, and drawing it here would answer one nobody asked.
  const sharedMaps = useMemo(() => {
    const theirs = new Set((agg?.maps ?? []).filter((m) => m.nades?.length).map((m) => m.map))
    return (ourMaps ?? []).filter((m) => theirs.has(m.map) && m.nades?.length)
  }, [ourMaps, agg])

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <button onClick={onBack} className="text-xs text-text-dim hover:text-accent">
          ← scouting
        </button>
        {canCoach && (
          <ConfirmButton
            label="delete folder"
            question={`Delete “${opp?.name ?? 'this folder'}” and its scouting demos?`}
            onConfirm={del}
            className="text-xs text-text-faint hover:text-loss"
          />
        )}
      </div>

      <div className="flex items-baseline gap-3">
        <h2 className="font-display text-xl font-bold uppercase tracking-wider text-text">{opp?.name ?? '…'}</h2>
        <span className="text-xs text-text-faint">
          {opp?.demos.length ?? 0} demo{(opp?.demos.length ?? 0) === 1 ? '' : 's'}
        </span>
      </div>

      {/* Veto sheet — pure FACEIT metadata, so this works for a team we've never recorded. That's
          why it sits above the demos rather than under them. */}
      {(veto || vetoErr) && (
        <section className="border border-border bg-surface p-5" style={card}>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <SectionLabel n="S1" label="Map veto" />
            {veto && (
              <span className="text-[11px] text-text-faint">
                {/* The sheet's own sample, not the teams' whole history — the rows only cover the
                    active pool, so the lifetime totals stopped adding up to what's on screen. */}
                us {veto.sample?.us ?? veto.us.matches} maps · them {veto.sample?.them ?? veto.them.matches} ·{' '}
                <button onClick={() => loadVeto(true)} className="text-text-dim hover:text-accent">
                  rebuild
                </button>
              </span>
            )}
          </div>

          {vetoErr === 'no_faceit_team' ? (
            <p className="mt-3 text-xs text-text-faint">
              This folder has no FACEIT team linked — it was opened from a demo, which only carries a name. The
              sheet needs their team page. Create the folder from their FACEIT team link, or play them in a
              league fixture and it links itself.
            </p>
          ) : vetoErr ? (
            <p className="mt-3 text-xs text-loss">Couldn’t build the sheet ({vetoErr}).</p>
          ) : veto ? (
            <>
              {/* A pool that rotated while nobody was looking produces a sheet that looks entirely
                  correct. Saying when it was last checked is the only honest signal available. */}
              {veto.pool?.stale && (
                <p className="mt-3 text-xs text-loss" role="alert">
                  The vetoable map pool hasn't been checked since {veto.pool.confirmed} (
                  {veto.pool.ageDays} days). If Valve has rotated it since, this sheet is missing a map
                  or offering one you can't pick.
                </p>
              )}
              <div className="mt-3 flex flex-wrap gap-4 text-sm">
                <span>
                  <span className="text-[11px] uppercase tracking-wider text-text-faint">Ban</span>{' '}
                  <span className="font-display" style={{ color: 'var(--loss)' }}>
                    {veto.banFirst.map((m) => m.replace('de_', '')).join(' · ') || '—'}
                  </span>
                </span>
                <span>
                  <span className="text-[11px] uppercase tracking-wider text-text-faint">Pick</span>{' '}
                  <span className="font-display text-accent">
                    {veto.pickFirst.map((m) => m.replace('de_', '')).join(' · ') || '—'}
                  </span>
                </span>
              </div>

              <div className="mt-3 overflow-x-auto">
                <table className="w-full min-w-140 border-collapse text-sm">
                  <thead>
                    <tr className="text-[11px] uppercase tracking-wider text-text-faint">
                      <th className="px-2 py-1.5 text-left font-medium">Map</th>
                      <th className="px-2 py-1.5 text-right font-medium">Us</th>
                      <th className="px-2 py-1.5 text-right font-medium">Them</th>
                      <th className="px-2 py-1.5 text-right font-medium" title="Our win% minus theirs">Edge</th>
                      <th className="px-2 py-1.5 text-left font-medium">Call</th>
                      <th className="px-2 py-1.5 text-left font-medium">Why</th>
                    </tr>
                  </thead>
                  <tbody>
                    {veto.rows.map((r) => (
                      <tr key={r.map} className="border-t border-border/60 hover:bg-surface-2">
                        <td className="px-2 py-2 font-medium text-text">{r.map.replace('de_', '')}</td>
                        <td className="px-2 py-2 text-right tabular-nums text-text-dim">
                          {r.us ? `${r.us.wins}-${r.us.losses} · ${r.us.winPct}%` : '—'}
                        </td>
                        <td className="px-2 py-2 text-right tabular-nums text-text-dim">
                          {r.them ? `${r.them.wins}-${r.them.losses} · ${r.them.winPct}%` : '—'}
                        </td>
                        <td
                          className="px-2 py-2 text-right tabular-nums"
                          style={{ color: (r.edge ?? 0) > 0 ? 'var(--accent)' : (r.edge ?? 0) < 0 ? 'var(--loss)' : 'var(--text-dim)' }}
                        >
                          {r.edge === null ? '—' : `${r.edge > 0 ? '+' : ''}${r.edge}`}
                        </td>
                        <td className="px-2 py-2">
                          <span
                            className="rounded px-1.5 py-px text-[10px] uppercase tracking-wider"
                            style={{
                              background:
                                r.call === 'ban' ? 'var(--loss)' : r.call === 'pick' ? 'var(--accent)' : 'var(--border)',
                              color:
                                r.call === 'ban' ? '#fff' : r.call === 'pick' ? 'var(--accent-ink)' : 'var(--text-dim)',
                            }}
                            title={`confidence: ${r.confidence}`}
                          >
                            {r.call}
                          </span>
                          {r.confidence !== 'high' && (
                            <span className="ml-1 text-[10px] text-text-faint">{r.confidence}</span>
                          )}
                        </td>
                        <td className="px-2 py-2 text-[11px] text-text-faint">{r.why}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="mt-2 text-[11px] text-text-faint">
                {/* Named, not assumed: a Nuke-liiga side's league games exist only in the esplay
                    catalog, and this line used to credit FACEIT for them. */}
                {sourceLabel(veto.sources)} — no demos involved. Win rate only, so a 13-11 and a 13-2 count the
                same. A call we don’t trust is shown as “thin” rather than a recommendation.
              </p>
            </>
          ) : null}
        </section>
      )}

      {/* Upload opponent demos + assign — filling a folder is coach work, and every route it
          calls is coach-gated server-side. Players still see the folder's demo list below it. */}
      {canCoach && (
      <section className="border border-border bg-surface p-5" style={card}>
        <SectionLabel n="S2" label="Add the opponent’s demos" />

        {/* Their games anywhere, not only against us. A league side that also plays ESEA has demos
            sitting in competitions we are not in, and before this the only way to know was to go
            and look. Behind a button because it is a roster crawl against a rate-limited API. */}
        {opp?.faceitTeamId && <TeamGames teamId={opp.faceitTeamId} name={opp.name} />}

        {/* direct upload (kept out of the Matches list) */}
        <div
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => {
            e.preventDefault()
            onFiles(Array.from(e.dataTransfer.files))
          }}
          onClick={() => up.s !== 'working' && inputRef.current?.click()}
          className="mt-4 flex cursor-pointer flex-col items-center justify-center border border-dashed border-border-bright py-8 text-center hover:bg-surface-2"
          style={{ borderRadius: 'var(--radius)' }}
        >
          <input
            ref={inputRef}
            type="file"
            accept=".dem,.gz,.zst"
            multiple
            className="hidden"
            onChange={(e) => onFiles(Array.from(e.target.files ?? []))}
          />
          {up.s === 'working' ? (
            <div className="w-72">
              <div className="text-sm text-accent">
                {up.label}… {up.label.startsWith('Parsing') ? `${elapsed}s` : `${up.pct}%`}
              </div>
              {up.label.startsWith('Parsing') ? (
                <div className="mt-1 text-xs text-text-faint">a full match takes ~1–2 min to parse — hang tight</div>
              ) : (
                <div className="mt-2 h-1.5 w-full overflow-hidden bg-border" style={{ borderRadius: '2px' }}>
                  <div className="h-full bg-accent" style={{ width: `${up.pct}%` }} />
                </div>
              )}
            </div>
          ) : (
            <>
              <div className="font-display text-sm text-text">Drop the opponent’s demos here</div>
              <div className="mt-1 text-xs text-text-faint">
                one or many · .dem / .dem.zst · they stay in this folder, out of Matches
              </div>
            </>
          )}
        </div>
        {up.s === 'error' && <p className="mt-2 text-xs text-loss">{up.msg}</p>}

        {/* pick the scouted team for each uploaded demo whose teams didn't match the folder name */}
        {pending.length > 0 && (
          <div className="mt-3 flex flex-wrap items-center gap-2 border border-border bg-bg-2 p-3" style={{ borderRadius: 'var(--radius)' }}>
            <span className="text-xs text-text-dim">
              Which team is {opp?.name}?{pending.length > 1 ? ` (${pending.length} left)` : ''}
            </span>
            {pending[0].teams.map((t) => (
              <button
                key={t}
                onClick={() => confirmPending(t)}
                className="border border-border-bright px-3 py-1 text-xs text-accent hover:bg-surface-2"
                style={{ borderRadius: 'var(--radius)' }}
              >
                {t}
              </button>
            ))}
          </div>
        )}

        {/* or assign an existing match we already played them */}
        <div className="mt-4 text-[11px] uppercase tracking-wider text-text-faint">Or add a match we already played</div>
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <select
            value={selDemo}
            onChange={(e) => setSelDemo(e.target.value)}
            className={inputCls}
            style={{ borderRadius: 'var(--radius)' }}
          >
            <option value="">Select a parsed match…</option>
            {available.map((m) => {
              const [a, b] = Object.entries(m.teamScore)
              return (
                <option key={m.id} value={m.id}>
                  {m.map} · {a?.[0]} {a?.[1]}–{b?.[1]} {b?.[0]}
                </option>
              )
            })}
          </select>
          {teams.length > 0 && (
            <>
              <span className="text-xs text-text-faint">scouted team:</span>
              <select
                value={selTeam}
                onChange={(e) => setSelTeam(e.target.value)}
                className={inputCls}
                style={{ borderRadius: 'var(--radius)' }}
              >
                {teams.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </>
          )}
          <button
            onClick={add}
            disabled={!selDemo || !selTeam}
            className="border border-border-bright px-4 py-2 text-sm text-accent hover:bg-surface-2 disabled:opacity-40"
            style={{ borderRadius: 'var(--radius)' }}
          >
            Add
          </button>
        </div>
        {/* assigned demos */}
        {opp && opp.demos.length > 0 && (
          <div className="mt-4 divide-y divide-border">
            {opp.demos.map((d) => (
              <div key={d.demoId} className="flex items-center gap-3 py-2 text-xs">
                <span className="font-display uppercase tracking-wider text-text-dim">{d.map}</span>
                <span className="text-text-faint">scouting {d.scoutedTeam}</span>
                <button onClick={() => removeDemo(d.demoId)} className="ml-auto px-1 text-text-faint hover:text-loss">
                  ✕
                </button>
              </div>
            ))}
          </div>
        )}
      </section>
      )}

      {/* Per-map aggregate */}
      {agg && agg.maps.length > 0 && (
        <section className="border border-border bg-surface p-5" style={card}>
          <SectionLabel n="S3" label="Tendencies by map" />
          <div className="mt-4 grid gap-3 md:grid-cols-2">
            {agg.maps.map((m) => (
              <MapCard key={m.map} m={m} />
            ))}
          </div>
        </section>
      )}

      {opp && opp.demos.length > 1 && (
        <section className="border border-border bg-surface p-5" style={card}>
          <SectionLabel n="S3c" label="What changed between two demos" />
          <p className="mt-2 text-xs text-text-faint">
            The folder above averages every demo you hold, which is what hides a change — a team that
            stopped hitting B looks like a team that half-does both. This subtracts one from another.
          </p>
          <DemoDiff opponentId={opp.id} demos={opp.demos} />
        </section>
      )}

      {agg && agg.maps.some((m) => m.econDepth?.rounds) && (
        <section className="border border-border bg-surface p-5" style={card}>
          <SectionLabel n="S3b" label="Their economy, past the headline" />
          <Economy maps={agg.maps} who="they" />
        </section>
      )}

      {/* Utility signature — the picture the AI report could only describe in words. */}
      {agg && agg.maps.some((m) => m.nades?.length) && (
        <section className="border border-border bg-surface p-5" style={card}>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <SectionLabel n="S4" label="Where their utility lands" />
            {/* Their setup only means something next to one you recognise. Both folds have
                produced this all along, on different pages, so the comparison a coach makes is
                "remember what ours looked like" — from memory, a tab away. */}
            <button
              onClick={() => setCompareUtil((v) => !v)}
              className="text-[11px] text-text-dim hover:text-accent"
              aria-pressed={compareUtil}
            >
              {compareUtil ? 'hide ours' : 'compare with ours'}
            </button>
          </div>
          <UtilMap maps={agg.maps} who={`${opp?.name ?? 'their'}'s`} />
          {compareUtil &&
            (ourMaps === null ? (
              <p className="mt-4 text-xs text-text-dim">Loading ours…</p>
            ) : sharedMaps.length === 0 ? (
              <p className="mt-4 text-xs text-text-faint">
                We have no parsed utility of our own on{' '}
                {agg.maps
                  .filter((m) => m.nades?.length)
                  .map((m) => m.map.replace('de_', ''))
                  .join(', ')}{' '}
                — nothing to hold this against yet.
              </p>
            ) : (
              <div className="mt-5 border-t border-border pt-4">
                <p className="mb-2 text-[11px] uppercase tracking-wider text-text-faint">
                  Ours, same maps
                </p>
                <UtilMap maps={sharedMaps} who="our" />
              </div>
            ))}
        </section>
      )}

      {agg && agg.maps.some((m) => m.spacingCurve && Object.keys(m.spacingCurve.T).length > 1) && (
        <section className="border border-border bg-surface p-5" style={card}>
          <SectionLabel n="S4b" label="How they spread over the round" />
          <SpacingCurve maps={agg.maps} who={`${opp?.name ?? 'their'}`} />
        </section>
      )}

      {/* Lineups — before the players table, because it decides how much of it to believe. */}
      {agg?.lineups && agg.lineups.length > 1 && <Lineups rows={agg.lineups} who="them" />}

      {/* Players */}
      {agg && agg.players.length > 0 && (
        <section className="border border-border bg-surface p-5" style={card}>
          <SectionLabel n="S5" label="Players" />
          <div className="mt-3 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wider text-text-faint">
                  <th className="py-1 pr-3">Player</th>
                  <th className="px-2 text-right">Rating</th>
                  <th className="px-2 text-right">ADR</th>
                  <th className="px-2 text-right">K–D</th>
                  <th className="px-2 text-right" title="Rounds with a kill, assist, survival or traded death">KAST</th>
                  <th className="px-2 text-right">Open CT/T</th>
                  <th className="px-2 text-right">AWP</th>
                  <th className="px-2 text-right" title="Trade kills / their deaths that got traded / deaths nobody traded">Trades</th>
                  <th className="px-2 text-right" title="Accuracy · headshot %">Acc·HS</th>
                  <th className="px-2 text-right" title="Clutches won/played — hover a cell for the 1v1..1v5 split">Clutch</th>
                  <th className="px-2 text-right">Top wpn</th>
                  <th className="px-2 text-right">Demos</th>
                </tr>
              </thead>
              <tbody className="tabular-nums">
                {agg.players.map((p) => (
                  <tr key={p.steamid64 || p.name} className="border-t border-border">
                    <td className="py-1.5 pr-3 text-text">
                      {p.name}
                      {/* Folded by steamid, so a mid-season rename is one player here. */}
                      {p.aliases?.length > 0 && (
                        <div className="text-[10px] text-text-faint" title={p.aliases.join(', ')}>
                          aka {p.aliases.slice(0, 2).join(', ')}
                          {p.aliases.length > 2 ? ` +${p.aliases.length - 2}` : ''}
                        </div>
                      )}
                    </td>
                    <td className="px-2 text-right text-accent">{p.rating.toFixed(2)}</td>
                    <td className="px-2 text-right text-text-dim">{p.adr}</td>
                    <td className="px-2 text-right text-text-dim">
                      {p.kills}–{p.deaths}
                    </td>
                    <td className="px-2 text-right text-text-dim">{p.kast}%</td>
                    <td className="px-2 text-right text-text-dim">
                      {p.openingCT.k}-{p.openingCT.d} / {p.openingT.k}-{p.openingT.d}
                    </td>
                    <td className="px-2 text-right text-text-dim">{p.awpKills || '–'}</td>
                    <td
                      className="px-2 text-right text-[11px]"
                      title={`${p.tradeKills} trade kills · ${p.tradedDeaths} of their deaths traded · ${p.untradedDeaths} untraded — high untraded means they die alone`}
                    >
                      <span className="text-text-dim">
                        {p.tradeKills}/{p.tradedDeaths}/
                      </span>
                      <span className="text-accent">{p.untradedDeaths}</span>
                    </td>
                    <td className="px-2 text-right text-[11px] text-text-dim">
                      {p.acc}%·{p.hsPct}%
                    </td>
                    <td className="px-2 text-right text-text-dim" title={clutchTitle(p.clutchBy)}>
                      {p.clutchP ? `${p.clutchW}/${p.clutchP}` : '–'}
                    </td>
                    <td className="px-2 text-right text-text-faint">{p.topWeapon}</td>
                    <td className="px-2 text-right text-text-faint">{p.demos}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {/* AI scouting report */}
      <section className="border border-border bg-surface p-5" style={card}>
        <div className="flex items-center justify-between">
          <SectionLabel n="AI" label="Scouting report" />
          {canCoach && (report || err) && (
            <button
              onClick={() => loadReport(true)}
              disabled={loading}
              className="text-xs text-text-dim hover:text-accent disabled:opacity-50"
            >
              {loading ? 'analysing…' : 'regenerate'}
            </button>
          )}
        </div>
        {/* An empty folder is answered before anything is waited on. There is nothing to analyse —
            the API returns `report: null` without calling the model — so "Analysing… ~10s" was a
            promise the page could not keep, shown for as long as the request happened to take and
            with the folder's name still blank because `opp` had not arrived either. */}
        {!opp || opp.demos.length === 0 ? (
          <p className="mt-3 text-sm text-text-faint">Add at least one match to generate a scouting report.</p>
        ) : loading && !report ? (
          <p className="mt-4 text-sm text-accent">
            Analysing {opp.name} across {opp.demos.length} demo(s)… ~10s.
          </p>
        ) : err ? (
          <p className="mt-3 text-xs text-loss">{err}</p>
        ) : !opp?.demos.length ? (
          <p className="mt-3 text-sm text-text-faint">Add at least one match to generate a scouting report.</p>
        ) : report ? (
          <div className="mt-4 space-y-4">
            <p className="text-sm leading-relaxed text-text">{report.overview}</p>
            {report.maps.map((m) => {
              // The prose can hedge or not; this can't. n comes from the aggregate, not the model.
              const n = agg?.maps.find((x) => x.map === m.map)?.demos ?? 0
              const thin = n > 0 && n < 3
              return (
              <div key={m.map} className="border border-border bg-surface-2 p-4" style={{ borderRadius: 'var(--radius)' }}>
                <div className="flex items-center gap-2">
                  <div className="font-display text-sm uppercase tracking-wider text-accent">{m.map}</div>
                  {n > 0 && (
                    <span
                      title={
                        thin
                          ? `Only ${n} demo${n === 1 ? '' : 's'} on this map — treat it as an observation, not a tendency`
                          : `${n} demos on this map`
                      }
                      className="rounded px-1.5 py-px text-[10px] uppercase tracking-wider"
                      style={{
                        background: thin ? 'var(--loss)' : 'var(--border)',
                        color: thin ? '#fff' : 'var(--text-dim)',
                      }}
                    >
                      {n} demo{n === 1 ? '' : 's'}
                      {thin ? ' · thin' : ''}
                    </span>
                  )}
                </div>
                <p className="mt-2 text-xs leading-relaxed text-text-dim">{m.read}</p>
                <div className="mt-2 grid gap-2 md:grid-cols-2">
                  <Line label="T side" text={m.tSide} />
                  <Line label="CT side" text={m.ctSide} />
                  <Line label="Sites" text={m.siteTendency} />
                  <Line label="Players here" text={m.keyPlayers} />
                  <Line label="How to beat" text={m.howToBeat} accent />
                </div>
              </div>
              )
            })}
            {report.players?.length > 0 && (
              <div>
                <div className="font-display text-xs uppercase tracking-wider text-text-dim">Player tendencies</div>
                <div className="mt-2 grid gap-2 md:grid-cols-2">
                  {report.players.map((p) => (
                    <div key={p.name} className="border border-border bg-surface-2 p-3" style={{ borderRadius: 'var(--radius)' }}>
                      <div className="flex items-baseline justify-between gap-2">
                        <span className="font-display text-xs uppercase tracking-wider text-text">{p.name}</span>
                        <span className="text-[10px] text-accent">{p.role}</span>
                      </div>
                      <p className="mt-1 text-xs leading-relaxed text-text-dim">{p.tendencies}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {report.keyTakeaways?.length > 0 && (
              <div>
                <div className="font-display text-xs uppercase tracking-wider text-text-dim">Key takeaways</div>
                <ul className="mt-2 space-y-1.5">
                  {report.keyTakeaways.map((t, i) => (
                    <li key={i} className="flex gap-2 text-xs leading-relaxed text-text">
                      <span aria-hidden className="mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full" style={{ background: 'var(--accent)' }} />
                      <span>{t}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <p className="text-[11px] text-text-faint">
              Generated by {report.model ?? 'AI'}
              {report.generatedAt ? ` · ${fmtStamp(report.generatedAt)}` : ''}. Scouting from limited
              demos — treat as a prep aid, not certainty.
            </p>
            {scouting?.reportStale && (
              // The tables above rebuilt themselves; this text didn't. Saying so beats letting a
              // coach assume the prose describes the numbers it's sitting next to.
              <p className="text-[11px] text-loss">
                These demos have been re-parsed since this was written — the tables above are
                current, the text isn't. Hit <b>regenerate</b> to bring it up to date.
              </p>
            )}
          </div>
        ) : null}
      </section>
    </div>
  )
}

function Line({ label, text, accent }: { label: string; text: string; accent?: boolean }) {
  return (
    <p className="text-xs leading-relaxed">
      <span className={accent ? 'text-accent' : 'text-text-faint'}>{label}: </span>
      <span className="text-text-dim">{text}</span>
    </p>
  )
}

function MapCard({ m }: { m: ScoutMapAgg }) {
  const pct = (v: { w: number; p: number }) => (v.p ? Math.round((100 * v.w) / v.p) : 0)
  const cv = (b: { n: number; won: number }) => (b.n ? `${Math.round((100 * b.won) / b.n)}% of ${b.n}` : '—')
  return (
    <div className="border border-border bg-surface-2 p-4" style={{ borderRadius: 'var(--radius)' }}>
      <div className="flex items-baseline justify-between">
        <span className="font-display text-sm uppercase tracking-wider text-text">{m.map}</span>
        <span className="text-xs text-text-faint">
          {m.wins}–{m.losses} · {m.roundWinPct}% rounds · {m.demos} demo{m.demos === 1 ? '' : 's'}
          {m.demos < 3 ? <span className="ml-1 text-loss">thin</span> : null}
        </span>
      </div>
      <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-text-faint">Opening duels</div>
          <div className="mt-0.5 tabular-nums text-text-dim">
            CT <span className="text-text">{m.opening.CT.k}–{m.opening.CT.d}</span> · T{' '}
            <span className="text-text">{m.opening.T.k}–{m.opening.T.d}</span>
          </div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-text-faint">Utility (total)</div>
          <div className="mt-0.5 tabular-nums text-text-dim">
            {m.util.flashes}F · {m.util.smokes}S · {m.util.he}HE · {m.util.molly}M
          </div>
        </div>
      </div>
      {m.sites.tRounds > 0 && (
        <div className="mt-2">
          <div className="text-[10px] uppercase tracking-wider text-text-faint">
            T-side sites · {m.sites.plants}/{m.sites.tRounds} plants ({Math.round((100 * m.sites.plants) / m.sites.tRounds)}%)
          </div>
          {Object.keys(m.sites.bySite).length > 0 ? (
            <div className="mt-1 space-y-1">
              {Object.entries(m.sites.bySite)
                .sort()
                .map(([s, n]) => {
                  const total = Object.values(m.sites.bySite).reduce((a, b) => a + b, 0) || 1
                  const p = Math.round((100 * n) / total)
                  return (
                    <div key={s} className="flex items-center gap-2 text-[10px]">
                      <span className="w-3 font-display text-text">{s}</span>
                      <div className="h-1.5 flex-1 overflow-hidden bg-border" style={{ borderRadius: '2px' }}>
                        <div className="h-full" style={{ width: `${p}%`, background: 'var(--accent)' }} />
                      </div>
                      <span className="w-16 text-right tabular-nums text-text-dim">{p}% ({n})</span>
                    </div>
                  )
                })}
            </div>
          ) : (
            <div className="mt-0.5 text-[10px] text-text-faint">A/B labels not mapped for {m.map} yet</div>
          )}
        </div>
      )}
      {Object.keys(m.economy).length > 0 && (
        <div className="mt-2">
          <div className="text-[10px] uppercase tracking-wider text-text-faint">Buy win-rate</div>
          <div className="mt-1 flex flex-wrap gap-1">
            {['PISTOL', 'ECO', 'FORCE', 'FULL']
              .filter((t) => m.economy[t]?.p)
              .map((t) => (
                <span
                  key={t}
                  className="rounded px-1.5 py-px text-[10px] tabular-nums text-text-dim"
                  style={{ background: 'var(--border)' }}
                >
                  {t} {pct(m.economy[t])}% <span className="text-text-faint">({m.economy[t].w}/{m.economy[t].p})</span>
                </span>
              ))}
          </div>
        </div>
      )}
      {m.players.length > 0 && (
        <div className="mt-2">
          <div className="text-[10px] uppercase tracking-wider text-text-faint">Players on this map</div>
          <div className="mt-1 space-y-0.5">
            {m.players.map((p) => (
              <div key={p.steamid64 || p.name} className="flex items-center gap-2 text-[10px] tabular-nums">
                <span
                  className="w-20 truncate text-text"
                  title={p.aliases?.length ? `aka ${p.aliases.join(', ')}` : undefined}
                >
                  {p.name}
                </span>
                <span className="text-accent">{p.rating.toFixed(2)}</span>
                <span className="text-text-faint">{p.adr} adr</span>
                <span className="text-text-dim" title="opening duels CT / T">
                  {p.openingCT.k}-{p.openingCT.d}/{p.openingT.k}-{p.openingT.d}
                </span>
                {p.awpKills > 0 && <span className="text-text-faint">{p.awpKills} awp</span>}
                <span className="ml-auto truncate text-text-faint">{p.topWeapon}</span>
              </div>
            ))}
          </div>
        </div>
      )}
      {/* The joins — how they convert first blood, and what happens once the bomb is down. */}
      <div className="mt-3 grid grid-cols-2 gap-x-4 gap-y-1 border-t border-border/60 pt-2 text-[11px]">
        <span className="flex justify-between gap-2">
          <span className="text-text-faint">Win opening → round</span>
          <span className="tabular-nums text-text-dim">{cv(m.conv.openWon)}</span>
        </span>
        <span className="flex justify-between gap-2">
          <span className="text-text-faint">Lose opening → round</span>
          <span className="tabular-nums text-text-dim">{cv(m.conv.openLost)}</span>
        </span>
        <span className="flex justify-between gap-2">
          <span className="text-text-faint">Post-plant closed</span>
          <span className="tabular-nums text-text-dim">{cv(m.conv.postPlant)}</span>
        </span>
        <span className="flex justify-between gap-2">
          <span className="text-text-faint">Retakes won</span>
          <span className="tabular-nums text-text-dim">{cv(m.conv.retake)}</span>
        </span>
        {(m.toSite ?? []).map((ts) => {
          const md = median(ts.secs)
          return md === null ? null : (
            <span key={ts.site} className="flex justify-between gap-2">
              <span className="text-text-faint">Pace to {ts.site}</span>
              <span className="tabular-nums text-text-dim" title={`median over ${ts.secs.length} rounds`}>
                {md.toFixed(0)}s · {paceLabel(md)}
              </span>
            </span>
          )
        })}
        {m.spacing?.T?.n > 0 && (
          <span className="flex justify-between gap-2">
            <span className="text-text-faint" title="Mean distance between living teammates — stacked or spread">
              Spacing T · CT
            </span>
            <span className="tabular-nums text-text-dim">
              {Math.round(m.spacing.T.sum / m.spacing.T.n)} ·{' '}
              {m.spacing.CT.n ? Math.round(m.spacing.CT.sum / m.spacing.CT.n) : '—'}
            </span>
          </span>
        )}
      </div>
    </div>
  )
}
